'use client';

import { useState, useEffect } from 'react';

interface PrivacyClientProps {
  lang: 'en' | 'de';
}

export default function PrivacyClient({ lang: initialLang }: PrivacyClientProps) {
  const [currentLanguage, setCurrentLanguage] = useState<'en' | 'de'>(initialLang);

  useEffect(() => {
    const handleLanguageChange = (event: CustomEvent<{ language: 'en' | 'de' }>) => {
      setCurrentLanguage(event.detail.language);
    };
    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange as EventListener);
    };
  }, []);

  const allContent = {
    en: {
      title: 'Privacy Policy',
      subtitle: 'Data Protection Information pursuant to Art. 13 GDPR (DSGVO)',
      lastUpdated: 'Last updated: March 10, 2026',
      sections: [
        {
          title: '1. Controller',
          content: [
            'Stefan Artmann\nPlanet Pulse Studios\nLütjenmoor 32, 22850 Norderstedt, Germany\nEmail: stefanartmann@planetpulsestudios.com\nPhone: +49 151 583 578 26',
            'The competent supervisory authority is the Unabhängiges Landeszentrum für Datenschutz Schleswig-Holstein (ULD), Holstenstraße 98, 24103 Kiel, mail@datenschutzzentrum.de.',
          ],
        },
        {
          title: '2. What Data We Collect and Why',
          intro: 'When you use planetpulsestudios.com or engage our services, we may collect:',
          bullets: [
            {
              label: 'Contact and project data:',
              text: 'Name, email address, phone number, company name and project-related information provided via contact forms or during service delivery. Legal basis: Art. 6(1)(b) GDPR (contract performance or pre-contractual steps) and Art. 6(1)(f) GDPR (legitimate interest in responding to inquiries).',
            },
            {
              label: 'Payment data:',
              text: 'Invoicing information including name, company name and address. We do not store payment card details. Legal basis: Art. 6(1)(b) GDPR and Art. 6(1)(c) GDPR (legal obligation for tax and accounting records).',
            },
            {
              label: 'Technical data:',
              text: 'IP address, browser type, referral source, pages visited and time on site, collected via privacy-compliant, cookieless analytics. Legal basis: Art. 6(1)(f) GDPR (legitimate interest in website improvement). No personal data is shared with advertising networks.',
            },
            {
              label: 'Business context data (project clients only):',
              text: 'Strategic business information, competitive data, market context and company-specific inputs provided by clients for the purpose of producing strategic analysis deliverables. This data is processed solely to deliver the contracted service. Legal basis: Art. 6(1)(b) GDPR.',
            },
          ],
        },
        {
          title: '3. Use of AI Tools in Service Delivery',
          content: [
            'Planet Pulse Studios uses AI research tools to assist in producing strategic deliverables. Business context information provided by clients may be processed through these tools as part of service delivery. All tools used operate under commercial or enterprise-tier agreements that include Data Processing Agreements (DPAs) pursuant to Art. 28 GDPR. We use only commercial or enterprise accounts — never consumer-tier subscriptions — for client data.',
            'Current AI tools used in service delivery:',
          ],
          bullets: [
            {
              label: 'Anthropic (Claude API):',
              text: 'Anthropic PBC, 548 Market St, PMB 90375, San Francisco, CA 94104, USA. DPA incorporated into Anthropic\'s Commercial Terms of Service including EU Standard Contractual Clauses (SCCs). Data transfers to the US are covered by the EU-US Data Privacy Framework and SCCs.',
            },
            {
              label: 'Perplexity (Enterprise/API):',
              text: 'Perplexity AI, Inc., San Francisco, CA, USA. DPA available at perplexity.ai/hub/legal/dpa including EU SCCs. Enterprise data is not used for model training.',
            },
          ],
          footer: 'Clients may request that specific business information not be processed through AI tools. In such cases, please notify us at the start of the engagement in writing.',
        },
        {
          title: '4. Third-Party Service Providers',
          intro: 'We use the following data processors. All operate under DPAs with appropriate safeguards for international transfers where applicable:',
          bullets: [
            {
              label: 'Hosting / Infrastructure:',
              text: 'Vercel Inc., 340 Pine Street Suite 701, San Francisco, CA 94104, USA. Website hosting. SCCs in place.',
            },
            {
              label: 'Email delivery:',
              text: 'Resend Inc., San Francisco, CA, USA. Transactional email delivery. SCCs in place.',
            },
            {
              label: 'Payment processing:',
              text: 'Stripe Inc., 510 Townsend Street, San Francisco, CA 94103, USA. Stripe\'s privacy policy applies to payment data. SCCs in place.',
            },
            {
              label: 'Analytics:',
              text: 'Plausible Analytics, Plausible Insights OÜ, Västriku tn 2, 50403 Tartu, Estonia. Cookieless, privacy-compliant analytics. No personal data transmitted. EU-hosted.',
            },
          ],
        },
        {
          title: '5. Data Retention',
          content: [
            'Project and contract data is retained for the duration of the business relationship plus the legally required retention period (10 years for tax and accounting records under § 147 AO and § 257 HGB).',
            'Contact inquiry data is deleted after 12 months unless a business relationship is established.',
            'Analytics data is anonymized and aggregated. No personal data is stored.',
            'AI tool processing: client business context data submitted to AI tools is processed in real time and not retained by Planet Pulse Studios beyond the project scope. Retention by AI tool providers is governed by their respective DPAs and commercial terms.',
          ],
        },
        {
          title: '6. International Data Transfers',
          content: [
            'Some service providers listed above are based in the United States. Data transfers to the US are made under the EU-US Data Privacy Framework (adequacy decision of July 10, 2023, upheld by the EU General Court in September 2025) and Standard Contractual Clauses (SCCs) pursuant to Art. 46(2)(c) GDPR. Details are available in the respective provider DPAs.',
          ],
        },
        {
          title: '7. Your Rights',
          intro: 'Under GDPR, you have the right to:',
          bullets: [
            { label: '', text: 'Access your personal data (Art. 15 GDPR).' },
            { label: '', text: 'Rectify inaccurate data (Art. 16 GDPR).' },
            { label: '', text: 'Request erasure (Art. 17 GDPR).' },
            { label: '', text: 'Restrict processing (Art. 18 GDPR).' },
            { label: '', text: 'Data portability (Art. 20 GDPR).' },
            { label: '', text: 'Object to processing based on legitimate interest (Art. 21 GDPR).' },
            { label: '', text: 'Withdraw consent at any time without affecting prior processing (Art. 7(3) GDPR).' },
          ],
          footer: 'To exercise these rights, contact: stefanartmann@planetpulsestudios.com\n\nYou also have the right to lodge a complaint with the ULD Schleswig-Holstein: mail@datenschutzzentrum.de, +49 431 988 1200.',
        },
        {
          title: '8. Cookies',
          content: [
            'This website uses only technically necessary cookies required for basic functionality. We do not use tracking cookies, advertising cookies, or third-party cookies. Our analytics tool (Plausible) is cookieless and collects no personal data.',
          ],
        },
        {
          title: '9. Trade Secrets and Confidentiality',
          content: [
            'Business context information provided by clients in the course of service delivery is treated as confidential. Where such information may constitute a trade secret under § 2 Nr. 1 GeschGehG, it is handled with appropriate security measures. Client business information is not shared with third parties beyond what is necessary for contracted service delivery and is not used for any purpose other than fulfilling the specific engagement for which it was provided.',
          ],
        },
        {
          title: '10. Changes to This Policy',
          content: [
            'We may update this policy. The date at the top reflects the latest version. Material changes will be communicated to active clients directly.',
          ],
        },
      ],
    },
    de: {
      title: 'Datenschutzerklärung',
      subtitle: 'Datenschutzinformationen gemäß Art. 13 DSGVO',
      lastUpdated: 'Zuletzt aktualisiert: 10. März 2026',
      sections: [
        {
          title: '1. Verantwortlicher',
          content: [
            'Stefan Artmann\nPlanet Pulse Studios\nLütjenmoor 32, 22850 Norderstedt, Deutschland\nE-Mail: stefanartmann@planetpulsestudios.com\nTelefon: +49 151 583 578 26',
            'Die zuständige Aufsichtsbehörde ist das Unabhängige Landeszentrum für Datenschutz Schleswig-Holstein (ULD), Holstenstraße 98, 24103 Kiel, mail@datenschutzzentrum.de.',
          ],
        },
        {
          title: '2. Welche Daten wir erheben und warum',
          intro: 'Wenn Sie planetpulsestudios.com nutzen oder unsere Dienstleistungen in Anspruch nehmen, können wir folgende Daten erheben:',
          bullets: [
            {
              label: 'Kontakt- und Projektdaten:',
              text: 'Name, E-Mail-Adresse, Telefonnummer, Unternehmensname und projektbezogene Informationen, die über Kontaktformulare oder im Rahmen der Leistungserbringung bereitgestellt werden. Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung oder vorvertragliche Maßnahmen) und Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der Beantwortung von Anfragen).',
            },
            {
              label: 'Zahlungsdaten:',
              text: 'Rechnungsinformationen einschließlich Name, Unternehmensname und Adresse. Wir speichern keine Zahlungskartendaten. Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO und Art. 6 Abs. 1 lit. c DSGVO (gesetzliche Verpflichtung für Steuer- und Buchführungsunterlagen).',
            },
            {
              label: 'Technische Daten:',
              text: 'IP-Adresse, Browsertyp, Verweisquelle, besuchte Seiten und Verweildauer, erhoben über datenschutzkonforme, cookielose Analysen. Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der Website-Verbesserung). Es werden keine personenbezogenen Daten an Werbenetzwerke weitergegeben.',
            },
            {
              label: 'Geschäftliche Kontextdaten (nur Projektkunden):',
              text: 'Strategische Geschäftsinformationen, Wettbewerbsdaten, Marktkontext und unternehmensspezifische Eingaben, die von Kunden zum Zweck der Erstellung strategischer Analyselieferungen bereitgestellt werden. Diese Daten werden ausschließlich zur Erbringung der vereinbarten Dienstleistung verarbeitet. Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO.',
            },
          ],
        },
        {
          title: '3. Einsatz von KI-Tools bei der Leistungserbringung',
          content: [
            'Planet Pulse Studios verwendet KI-Recherche-Tools zur Unterstützung bei der Erstellung strategischer Lieferungen. Geschäftliche Kontextinformationen, die von Kunden bereitgestellt werden, können im Rahmen der Leistungserbringung durch diese Tools verarbeitet werden. Alle verwendeten Tools werden unter kommerziellen oder Enterprise-Vereinbarungen betrieben, die Auftragsverarbeitungsverträge (AVV) gemäß Art. 28 DSGVO umfassen. Wir verwenden ausschließlich kommerzielle oder Enterprise-Konten – niemals Verbraucher-Abonnements – für Kundendaten.',
            'Derzeit verwendete KI-Tools bei der Leistungserbringung:',
          ],
          bullets: [
            {
              label: 'Anthropic (Claude API):',
              text: 'Anthropic PBC, 548 Market St, PMB 90375, San Francisco, CA 94104, USA. AVV ist in Anthropics kommerziellen Nutzungsbedingungen einschließlich EU-Standardvertragsklauseln (SCC) enthalten. Datenübermittlungen in die USA erfolgen im Rahmen des EU-US-Datenschutzrahmens und der SCC.',
            },
            {
              label: 'Perplexity (Enterprise/API):',
              text: 'Perplexity AI, Inc., San Francisco, CA, USA. AVV verfügbar unter perplexity.ai/hub/legal/dpa einschließlich EU-SCC. Enterprise-Daten werden nicht für das Modelltraining verwendet.',
            },
          ],
          footer: 'Kunden können beantragen, dass bestimmte Geschäftsinformationen nicht durch KI-Tools verarbeitet werden. In diesem Fall bitten wir um schriftliche Mitteilung zu Beginn des Auftrags.',
        },
        {
          title: '4. Drittanbieter',
          intro: 'Wir nutzen folgende Auftragsverarbeiter. Alle arbeiten unter AVV mit angemessenen Schutzmaßnahmen für internationale Übermittlungen, soweit zutreffend:',
          bullets: [
            {
              label: 'Hosting / Infrastruktur:',
              text: 'Vercel Inc., 340 Pine Street Suite 701, San Francisco, CA 94104, USA. Website-Hosting. SCC vorhanden.',
            },
            {
              label: 'E-Mail-Versand:',
              text: 'Resend Inc., San Francisco, CA, USA. Transaktionaler E-Mail-Versand. SCC vorhanden.',
            },
            {
              label: 'Zahlungsabwicklung:',
              text: 'Stripe Inc., 510 Townsend Street, San Francisco, CA 94103, USA. Stripes Datenschutzrichtlinie gilt für Zahlungsdaten. SCC vorhanden.',
            },
            {
              label: 'Analyse:',
              text: 'Plausible Analytics, Plausible Insights OÜ, Västriku tn 2, 50403 Tartu, Estland. Cookielose, datenschutzkonforme Analyse. Keine personenbezogenen Daten werden übermittelt. EU-gehostet.',
            },
          ],
        },
        {
          title: '5. Datenspeicherung',
          content: [
            'Projekt- und Vertragsdaten werden für die Dauer der Geschäftsbeziehung zuzüglich der gesetzlich vorgeschriebenen Aufbewahrungsfrist gespeichert (10 Jahre für Steuer- und Buchführungsunterlagen gemäß § 147 AO und § 257 HGB).',
            'Kontaktanfragedaten werden nach 12 Monaten gelöscht, sofern keine Geschäftsbeziehung begründet wird.',
            'Analysedaten werden anonymisiert und aggregiert. Es werden keine personenbezogenen Daten gespeichert.',
            'KI-Tool-Verarbeitung: Geschäftliche Kontextdaten von Kunden, die an KI-Tools übermittelt werden, werden in Echtzeit verarbeitet und von Planet Pulse Studios nicht über den Projektumfang hinaus gespeichert. Die Speicherung durch KI-Tool-Anbieter richtet sich nach deren jeweiligen AVV und kommerziellen Bedingungen.',
          ],
        },
        {
          title: '6. Internationale Datenübermittlungen',
          content: [
            'Einige der oben aufgeführten Dienstleister haben ihren Sitz in den Vereinigten Staaten. Datenübermittlungen in die USA erfolgen im Rahmen des EU-US-Datenschutzrahmens (Angemessenheitsbeschluss vom 10. Juli 2023, bestätigt durch das EU-Gericht im September 2025) und der Standardvertragsklauseln (SCC) gemäß Art. 46 Abs. 2 lit. c DSGVO. Details sind in den jeweiligen Anbieter-AVV verfügbar.',
          ],
        },
        {
          title: '7. Ihre Rechte',
          intro: 'Gemäß DSGVO haben Sie das Recht auf:',
          bullets: [
            { label: '', text: 'Auskunft über Ihre personenbezogenen Daten (Art. 15 DSGVO).' },
            { label: '', text: 'Berichtigung unrichtiger Daten (Art. 16 DSGVO).' },
            { label: '', text: 'Löschung Ihrer Daten (Art. 17 DSGVO).' },
            { label: '', text: 'Einschränkung der Verarbeitung (Art. 18 DSGVO).' },
            { label: '', text: 'Datenübertragbarkeit (Art. 20 DSGVO).' },
            { label: '', text: 'Widerspruch gegen die Verarbeitung auf Grundlage berechtigter Interessen (Art. 21 DSGVO).' },
            { label: '', text: 'Widerruf der Einwilligung jederzeit ohne Auswirkung auf die vorherige Verarbeitung (Art. 7 Abs. 3 DSGVO).' },
          ],
          footer: 'Zur Ausübung dieser Rechte wenden Sie sich an: stefanartmann@planetpulsestudios.com\n\nSie haben außerdem das Recht, eine Beschwerde beim ULD Schleswig-Holstein einzureichen: mail@datenschutzzentrum.de, +49 431 988 1200.',
        },
        {
          title: '8. Cookies',
          content: [
            'Diese Website verwendet ausschließlich technisch notwendige Cookies, die für die grundlegende Funktionalität erforderlich sind. Wir verwenden keine Tracking-Cookies, Werbe-Cookies oder Drittanbieter-Cookies. Unser Analyse-Tool (Plausible) ist cookielos und erhebt keine personenbezogenen Daten.',
          ],
        },
        {
          title: '9. Geschäftsgeheimnisse und Vertraulichkeit',
          content: [
            'Geschäftliche Kontextinformationen, die von Kunden im Rahmen der Leistungserbringung bereitgestellt werden, werden vertraulich behandelt. Soweit solche Informationen ein Geschäftsgeheimnis im Sinne von § 2 Nr. 1 GeschGehG darstellen können, werden sie mit angemessenen Sicherheitsmaßnahmen behandelt. Geschäftliche Kundeninformationen werden nicht an Dritte weitergegeben, soweit dies nicht für die vertraglich vereinbarte Leistungserbringung erforderlich ist, und werden nicht für andere Zwecke als die Erfüllung des jeweiligen Auftrags verwendet.',
          ],
        },
        {
          title: '10. Änderungen dieser Richtlinie',
          content: [
            'Wir können diese Richtlinie aktualisieren. Das Datum oben gibt die neueste Version an. Wesentliche Änderungen werden aktiven Kunden direkt mitgeteilt.',
          ],
        },
      ],
    },
  };

  const t = allContent[currentLanguage];

  return (
    <main className="relative z-10 pt-40 pb-20">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-secondary/50 backdrop-blur-md rounded-xl p-8 md:p-12 border border-border shadow-cosmic">
          <h1 className="font-exo text-heading-xl md:text-hero-mobile text-accent mb-4">
            {t.title}
          </h1>
          <p className="text-foreground-secondary text-body-lg mb-2">
            {t.subtitle}
          </p>
          <p className="text-foreground-secondary text-body-sm mb-12">
            {t.lastUpdated}
          </p>

          <div className="space-y-8">
            {t.sections.map((section, idx) => (
              <section key={idx}>
                <h2 className="font-exo text-heading-md text-highlight mb-3">
                  {section.title}
                </h2>

                {section.intro && (
                  <p className="text-foreground-light text-body-md leading-relaxed mb-3">
                    {section.intro}
                  </p>
                )}

                {section.content && section.content.map((para, pIdx) => (
                  <p key={pIdx} className="text-foreground-light text-body-md leading-relaxed mb-3 whitespace-pre-line">
                    {para}
                  </p>
                ))}

                {section.bullets && section.bullets.length > 0 && (
                  <ul className="space-y-3 mb-3">
                    {section.bullets.map((bullet, bIdx) => (
                      <li key={bIdx} className="text-foreground-light text-body-md leading-relaxed">
                        {bullet.label ? (
                          <>
                            <span className="text-highlight font-semibold">{bullet.label}</span>{' '}
                            {bullet.text}
                          </>
                        ) : (
                          <span className="flex items-start gap-2">
                            <span className="text-accent mt-1.5 shrink-0">•</span>
                            <span>{bullet.text}</span>
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {section.footer && (
                  <p className="text-foreground-light text-body-md leading-relaxed whitespace-pre-line">
                    {section.footer}
                  </p>
                )}
              </section>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}